// The Sounds page's generations (#235, live's `tabs/my-generations`): which jobs it lists, in
// live's words, and what a sound is called.
import type { GenerationOutput, GenerationType, Job } from "../../api/generated/model";
import { extensionOf, normalizeFileName } from "../../platform/jobs/file-names";

/**
 * Live's badge for each kind the composer makes (`GENERATION_TYPE_LABEL`). A talking head's
 * voiceover and a voice design are audio too, but live's Sounds tab never lists them.
 */
export const SOUND_LABEL: Readonly<Partial<Record<GenerationType, string>>> = {
  "audio.sfx": "Sound effects",
  "audio.tts": "Text-to-speech",
  "audio.music": "Music",
};

/** The kinds the tab asks the job list for: a page holds these alone, live's 5 (issue 784). */
export const SOUND_TYPES: readonly GenerationType[] = ["audio.sfx", "audio.tts", "audio.music"];

export function isSoundJob(job: Job): boolean {
  return SOUND_LABEL[job.type] !== undefined;
}

/** Live's words (`constants/audio.ts`, `pages/sounds/constants.ts`, `my-generations/constants`). */
/** A sound made, with its file at hand: its card plays it, the lightbox shows it. */
export function soundReady(job: Job, output: GenerationOutput): boolean {
  return job.status === "succeeded" && output.status === "delivered" && output.url !== null;
}

export const UNTITLED = "Untitled audio";
export const GENERATING = "Generating...";
export const FAILED_TITLE = "Generation failed";
export const FAILED_TEXT = "Something went wrong while generating this sound. Please try again.";

/** A sound's name: its text, as live's `getGenerationSoundName`, else "Untitled audio". */
export function soundName(job: Job): string {
  const text = job.params?.prompt?.trim() ?? "";
  return text === "" ? UNTITLED : text;
}

const NAME_MOST = 100;

/** Live's file names: `sound-<id>` for the generation's download, the sound's name for its own. */
export function generationFileName(job: Job, output: GenerationOutput): string {
  return `sound-${job.id}.${extensionOf(output.mime)}`;
}

/**
 * A sound's own file: its name by live's `normalizeFileName`, cut to 100 characters (a script
 * runs to 5,000), and its type; live's "Untitled audio" so normalized when nothing is left.
 */
export function soundFileName(job: Job, output: GenerationOutput): string {
  const base = normalizeFileName(soundName(job)).slice(0, NAME_MOST);
  return `${base === "" ? normalizeFileName(UNTITLED) : base}.${extensionOf(output.mime)}`;
}
